import { useEffect, useState } from "react";
import { Space, Col, message } from "antd";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import theme from "../../../helpers/theme";
import AddCustomTagButton from "./addCustomTagButton";
import EditCustomTag from "./editCustomTag";
import AddCustomGroupButton from "../bankAccountPlacementSettings/addCustomGroupButton";
import GenericDropdown from "../../../components/generic/genericDropdown";
import GenericDeleteModal from "../../../components/buttons/genericDeleteModal";
import EditGroup from "../bankAccountPlacementSettings/editCustomGroup";
import { backendPostWithoutRefresh } from "../../../helpers/callBackend";
import HeaderBox from "../../../components/generic/arpariBoxes/headerBox";
import PermissionGate from "../../../helpers/PermissionGate";


/**
 * Hiearchy creation and viewing tree table
 * 
 * @param {*} props 
 * @prop {number} group_type must be a valid HierarchyGroupType value
 * 
 */
const CustomTagsHierarchy = (props) => {
    const [groupedTags, setGroupedTags] = useState({});
    const TXN_TAG = 2;
    const BA_TAG = 1;


    const initializeSubmit = async () => {

        try {
            const res = await backendPostWithoutRefresh("/api/generic-tag/group/initialize/", {});
        } catch (error) {
            console.error(error);
        }
    };

    const organizeTagsByGroup = () => {
        const tagsByGroup = {};
        props.flattenedTags.forEach(tag => {
            const groupId = tag.group_id;
            if (!tagsByGroup[groupId]) {
                tagsByGroup[groupId] = [];
            }
            tagsByGroup[groupId].push(tag);
        });
        const result = {};
        Object.keys(props.groups || {}).forEach(groupKey => {
            const group = props.groups[groupKey];
            result[group.id] = {
                id: group.id,
                name: group.name,
                description: group.description,
                children: [],
            };
        });
        Object.keys(tagsByGroup).forEach(groupId => {
            if (result[groupId]) {
                result[groupId].children = buildTree(tagsByGroup[groupId]);
            }
        });
        return result;
    };

    const buildTree = (flattenedTags) => {
        const map = {};
        const roots = [];
        flattenedTags.forEach(item => {
            map[item.id] = { ...item, children: [] };
        });
        flattenedTags.forEach(item => {
            if (item.parent === null) {
                roots.push(map[item.id]);
            } else {
                map[item?.parent]?.children?.push(map[item.id]);
            }
        });
        return roots;
    };

    const nodeNameDescriptionFormatted = (nodeName, nodeDescription) => {

        return <>
            <Typography marginRight={1}>{nodeName}</Typography>
            <Typography fontStyle={"italic"} color={"text.disabled"}>{nodeDescription}</Typography>
        </>
    }

    useEffect(() => {
        setGroupedTags(organizeTagsByGroup());
    }, [props.flattenedTags, props.flattenedTags?.length, props.groups, props.groups?.length]);

    const renderTreeNodes = (nodes, isRoot = false, group = null) => {
        nodes = nodes.sort((a, b) => a.name.localeCompare(b.name))
        if (props.group_type !== TXN_TAG && group !== null) {
            return (
                <TreeItem
                    key={`group_${group.id}`}
                    itemId={`group_${group.id}`}
                    label={
                        <Col style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                            {nodeNameDescriptionFormatted(group.name, group.description)}
                            <Col style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                                <PermissionGate requiredRole='dashboardEditor'>
                                    <GenericDropdown
                                        dropdownItems={[
                                            <span onClick={(e) => e.stopPropagation()}>
                                                <EditGroup
                                                    groupId={group.id}
                                                    groupName={group.name}
                                                    groupDescription={group.description}
                                                    handleEditSuccess={(updatedGroup) => props.editGroup(updatedGroup)}
                                                />
                                            </span>,
                                            <GenericDeleteModal
                                                type='Group'
                                                title={`Are you sure you want to delete ${group.name}?`}
                                                URL='/api/generic-tag/group/'
                                                deleteParams={{ id: group.id }}
                                                description={`By clicking Delete below you will no longer have the group ${group.name} on your dashboard. If you do not want to proceed or are unsure, please click X on the upper right.`}
                                                handleDeleteSuccess={() => props.deleteGroup(group.id)}
                                                handleDeleteFailure={() => message.error(`Failed to delete the group ${group.name}`)}
                                            />
                                        ]}
                                        style={{ marginLeft: 10 }}
                                    />
                                </PermissionGate>
                            </Col>
                        </Col>
                    }
                >
                    {renderTreeNodes(nodes, false)}
                    <TreeItem
                        key={`add_child_tag_${group.id}`}
                        itemId={`add_child_tag_${group.id}`}
                        sx={!isRoot ? {
                            '&.MuiTreeItem-root': {
                                borderLeft: `1px dashed ${theme.palette.secondary.light}`,
                            }
                        } : undefined}
                        label={
                            <PermissionGate requiredRole='dashboardEditor'>
                                <span onClick={(e) => e.stopPropagation()}>
                                    <AddCustomTagButton
                                        groupType={props.group_type}
                                        groupId={group.id}
                                        handleAddSuccess={(tag) => props.addCustomTag(tag)}
                                        buttonVariant="text"
                                        buttonColor="primary"
                                        buttonLabel={<Typography>Add Child Tag for {group.name}</Typography>}
                                    />
                                </span>
                            </PermissionGate>
                        }>
                    </TreeItem>
                </TreeItem>
            );
        }

        return nodes.map((node) => (

            <TreeItem
                key={`tag_${node.id}`}
                itemId={`tag_${node.id}`}
                sx={!isRoot ? {
                    '&.MuiTreeItem-root': {
                        borderLeft: `1px dashed ${theme.palette.secondary.light}`,
                    }
                } : undefined}
                label={
                    <Col style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                        {nodeNameDescriptionFormatted(node.name, node.description)}
                        <Col style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                            <PermissionGate requiredRole='dashboardEditor'>
                                <GenericDropdown
                                    dropdownItems={[
                                        <span onClick={(e) => e.stopPropagation()}>
                                            <EditCustomTag
                                                tagId={node.id}
                                                groupId={node.group_id}
                                                groupName={node.group_name}
                                                tagGroups={props.groups}
                                                tagName={node.name}
                                                flattenedTags={props.flattenedTags}
                                                flattenedTagsLoading={props.flattenedTagsLoading}
                                                tagDescription={node.description}
                                                handleEditSuccess={(tag) => props.editCustomTag(tag)}
                                            />
                                        </span>,
                                        ...(node.children?.length === 0 ? [
                                            <GenericDeleteModal
                                                type='Custom Tag'
                                                title={`Are you sure you want to delete ${node.name}?`}
                                                URL='/api/generic-tag/'
                                                deleteParams={{ id: node.id }}
                                                description={`By clicking Delete below you will no longer have the custom tag ${node.name} on your dashboard. If you do not want to proceed or are unsure, please click X on the upper right.`}
                                                handleDeleteSuccess={() => props.deleteCustomTag(node.id)}
                                                handleDeleteFailure={() => message.error(`Failed to delete the custom tag ${node.name}`)}
                                            />
                                        ] : [])
                                    ]}
                                    style={{ marginLeft: 10 }}
                                />
                            </PermissionGate>
                        </Col>
                    </Col>
                }
            >
                {node.children?.length > 0 && renderTreeNodes(node.children, false)}
                <TreeItem
                    key={`add_child_tag_${node.id}`}
                    itemId={`add_child_tag_${node.id}`}
                    sx={!isRoot ? {
                        '&.MuiTreeItem-root': {
                            borderLeft: `1px dashed ${theme.palette.secondary.light}`,
                        }
                    } : undefined}
                    label={
                        <PermissionGate requiredRole='dashboardEditor'>
                            <span onClick={(e) => e.stopPropagation()}>
                                <AddCustomTagButton
                                    parentId={node.id}
                                    groupType={props.group_type}
                                    groupId={node.group_id}
                                    handleAddSuccess={(tag) => props.addCustomTag(tag)}
                                    buttonVariant="text"
                                    buttonColor="primary"
                                    buttonLabel={<Typography>Add Child Tag for {node.name}</Typography>}
                                />
                            </span>
                        </PermissionGate>
                    }>
                </TreeItem>
            </TreeItem>
        ));
    };

    const renderByGroups = () => {
        if (props.groups && props.groups?.length > 0) {
            return props.groups.sort((a, b) => a.name.localeCompare(b.name)).map(group => {
                const groupTags = groupedTags[group.id]?.children || [];

                return (
                    <Box
                        key={group.id}
                        sx={{
                            backgroundColor: "white",
                            border: `1px solid ${theme.palette.secondary.light}`,
                            borderRadius: 1,
                            padding: theme.spacing(1),
                            mt: 1
                        }}
                    >
                        <SimpleTreeView
                            defaultCollapseIcon={<RemoveIcon />}
                            defaultExpandIcon={<AddIcon />}
                            defaultEndIcon={<CloseIcon />}
                        >
                            {renderTreeNodes(groupTags, true, group)}
                        </SimpleTreeView>
                    </Box>
                );
            });
        } else {
            const allTags = [];
            Object.values(groupedTags).forEach(tagsArray => {
                allTags.push(...tagsArray?.children);
            });

            if (allTags?.length === 0 && !props.flattenedTagsLoading) {
                return (
                    <div style={{ padding: theme.spacing(1) }}>
                        No tags available
                    </div>
                );
            }

            return (
                <Box sx={{ marginBottom: theme.spacing(2) }}>
                    <SimpleTreeView
                        defaultCollapseIcon={<RemoveIcon />}
                        defaultExpandIcon={<AddIcon />}
                        defaultEndIcon={<CloseIcon />}
                    >
                        {renderTreeNodes(allTags, true)}
                    </SimpleTreeView>
                </Box>
            );
        }
    };

    return (
        <HeaderBox
            title={props.title}
            titleFontSize={14}
            actions={
                (props.group_type === TXN_TAG) ? (
                    <AddCustomTagButton
                        buttonLabel="+ Add New"
                        buttonVariant="contained"
                        buttonColor="primary"
                        groupType={props.group_type}
                        handleAddSuccess={(tag) => props.addCustomTag(tag)}
                    />
                ) : (
                    <>
                        {false &&
                            <Button
                                onClick={initializeSubmit}
                            >
                                Run Initializer
                            </Button>}
                        <AddCustomGroupButton
                            groupType={props.group_type}
                            handleAddSuccess={(group) => props.addGroup(group)}
                        />
                    </>
                )
            }
            body={
                <Space direction="vertical" style={{ width: "100%" }}>
                    {renderByGroups()}
                </Space>
            }
        />
    );
};

export default CustomTagsHierarchy;